import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, RefreshCw, Search, X, Globe, Bot, MessageSquare, StickyNote, Download, FileText } from "lucide-react";
import { BrandLogo } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { crmLeads, crmUpdateLead, rebuildPdfBlob, adminProposalLink } from "@/lib/api";
import { API } from "@/lib/api";
import OutreachPanel from "@/components/OutreachPanel";
import { toast } from "sonner";

const STATUS_META = {
  new: { label: "New", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  contacted: { label: "Contacted", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  qualified: { label: "Qualified", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  won: { label: "Won", cls: "bg-green-50 text-green-700 border-green-200" },
  lost: { label: "Lost", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};
const STATUSES = ["new", "contacted", "qualified", "won", "lost"];
const GOAL_LABEL = { customers: "Get more customers", modernize: "Modernize website", ai: "Add AI & automation" };

const fmtDate = (d) => (d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export default function LeadCrm() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { if (user === null || (user && user.role !== "admin")) navigate("/login"); }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await crmLeads({ ...(status && { status }), ...(source && { source }), ...(q && { q }) });
      setData(d);
    } catch { toast.error("Failed to load leads"); } finally { setLoading(false); }
  }, [status, source, q]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  if (user === undefined || user === null || user.role !== "admin")
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-brand" size={26} /></div>;

  const changeStatus = async (lead, newStatus) => {
    try {
      await crmUpdateLead({ lead_key: lead.lead_key, status: newStatus });
      toast.success(`Marked ${STATUS_META[newStatus].label}`);
      setData((d) => ({ ...d, leads: d.leads.map((l) => (l.lead_key === lead.lead_key ? { ...l, status: newStatus } : l)), pipeline: recount(d, lead.status, newStatus) }));
      if (selected?.lead_key === lead.lead_key) setSelected((s) => ({ ...s, status: newStatus }));
    } catch { toast.error("Update failed"); }
  };

  const recount = (d, from, to) => {
    const p = { ...d.pipeline };
    if (from && p[from] != null) p[from] = Math.max(0, p[from] - 1);
    if (to) p[to] = (p[to] || 0) + 1;
    return p;
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ ...(status && { status }), ...(source && { source }), ...(q && { q }) });
      const res = await fetch(`${API}/admin/leads/export.csv?${params}`, { headers: { Authorization: `Bearer ${localStorage.getItem("wt_token")}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `websitetech-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Exported CSV");
    } catch { toast.error("Export failed"); } finally { setExporting(false); }
  };

  const addNote = async () => {
    if (!note.trim() || !selected) return;
    setSavingNote(true);
    try {
      await crmUpdateLead({ lead_key: selected.lead_key, note });
      const entry = { text: note.trim(), at: new Date().toISOString(), by: user.email };
      setSelected((s) => ({ ...s, notes: [...(s.notes || []), entry] }));
      setData((d) => ({ ...d, leads: d.leads.map((l) => (l.lead_key === selected.lead_key ? { ...l, notes: [...(l.notes || []), entry] } : l)) }));
      setNote(""); toast.success("Note added");
    } catch { toast.error("Could not save note"); } finally { setSavingNote(false); }
  };

  const openReport = async (conceptId) => {
    try {
      const url = await rebuildPdfBlob(conceptId);
      const win = window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      if (!win) toast.error("Please allow pop-ups to open the report.");
    } catch { toast.error("Could not open the report"); }
  };

  const openProposal = async (proposalId) => {
    try {
      const { pdf_url } = await adminProposalLink(proposalId);
      const win = window.open(`${process.env.REACT_APP_BACKEND_URL}${pdf_url}`, "_blank", "noopener");
      if (!win) toast.error("Please allow pop-ups to open the proposal.");
    } catch { toast.error("Could not open the proposal"); }
  };

  const pipeline = data?.pipeline || {};
  const total = data?.total || 0;
  const sourceIcon = (s) => (s === "ai-assistant" ? Bot : s === "consultation" ? MessageSquare : Globe);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 z-30">
        <Link to="/"><BrandLogo size={34} /></Link>
        <div className="flex items-center gap-4">
          <Link to="/admin" className="text-sm text-slate-500 hover:text-brand">Console</Link>
          <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><StickyNote size={15} /> Leads CRM</span>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <Link to="/admin" className="text-slate-500 hover:text-brand text-sm flex items-center gap-1.5 mb-2"><ArrowLeft size={14} /> Back to console</Link>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Lead Management</h1>
            <p className="text-slate-500 mt-1 text-sm">{total} total leads across the funnel & AI assistant.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="btn-outline rounded-xl px-4 py-2.5 flex items-center gap-2 text-sm" data-testid="crm-refresh">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={exportCsv} disabled={exporting} className="btn-primary rounded-xl px-4 py-2.5 flex items-center gap-2 text-sm disabled:opacity-50" data-testid="crm-export-csv">
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Export CSV
            </button>
          </div>
        </div>

        {/* Pipeline filters */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6">
          <PipeCard active={status === ""} label="All" value={total} onClick={() => setStatus("")} testid="pipe-all" />
          {STATUSES.map((s) => (
            <PipeCard key={s} active={status === s} label={STATUS_META[s].label} value={pipeline[s] || 0} onClick={() => setStatus(status === s ? "" : s)} testid={`pipe-${s}`} accent={STATUS_META[s].cls} />
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, company…" data-testid="crm-search"
              className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-3 py-2.5 text-sm outline-none focus:border-brand" />
          </div>
          <select value={source} onChange={(e) => setSource(e.target.value)} data-testid="crm-source-filter"
            className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-brand">
            <option value="">All sources</option>
            <option value="website">Website</option>
            <option value="rebuild-preview">Makeover Preview</option>
            <option value="consultation">Consultation</option>
            <option value="ai-assistant">AI Assistant</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {data === null ? (
            <div className="p-12 flex justify-center"><Loader2 className="animate-spin text-brand" /></div>
          ) : data.leads.length === 0 ? (
            <p className="p-12 text-center text-slate-400 text-sm">No leads match these filters.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {data.leads.map((l) => {
                const SI = sourceIcon(l.source);
                return (
                  <div key={l.lead_key} className="p-4 flex items-center gap-4 hover:bg-slate-50/60 transition-colors cursor-pointer" data-testid="crm-lead-row" onClick={() => { setSelected(l); setNote(""); }}>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-800 text-sm truncate">{l.name}</p>
                      <p className="text-xs text-slate-400 truncate">{l.email || l.phone || "—"}{l.company && ` · ${l.company}`}</p>
                    </div>
                    <div className="hidden md:flex items-center gap-1.5 text-xs text-slate-400 w-32 shrink-0"><SI size={13} /> {l.source}</div>
                    <div className="hidden sm:block text-xs text-slate-400 w-32 shrink-0">{fmtDate(l.created_at)}</div>
                    <select value={l.status} onClick={(e) => e.stopPropagation()} onChange={(e) => changeStatus(l, e.target.value)} data-testid="crm-status-select"
                      className={`text-xs font-semibold border rounded-lg px-2 py-1.5 outline-none cursor-pointer shrink-0 ${STATUS_META[l.status]?.cls || ""}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Lead detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="crm-detail-drawer">
          <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-md bg-white h-full shadow-2xl overflow-y-auto animate-in slide-in-from-right">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
              <h2 className="font-heading font-bold text-slate-900 truncate">{selected.name}</h2>
              <button onClick={() => setSelected(null)} data-testid="crm-drawer-close" className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="space-y-2 text-sm">
                <Field label="Email" value={selected.email} />
                <Field label="WhatsApp" value={selected.whatsapp} />
                <Field label="Delivery" value={selected.method} />
                {selected.consent != null && selected.method === "whatsapp" && <Field label="WhatsApp consent" value={selected.consent ? "Given" : "Not given"} />}
                <Field label="Company" value={selected.company} />
                <Field label="Website" value={selected.website} />
                <Field label="Service interest" value={selected.service} />
                <Field label="Goal" value={GOAL_LABEL[selected.goal] || selected.goal} />
                {selected.website_score != null && <Field label="Opportunity score" value={String(selected.website_score)} />}
                {selected.geo && (selected.geo.city || selected.geo.country) && (
                  <Field label="Location" value={[selected.geo.city, selected.geo.state, selected.geo.country].filter(Boolean).join(", ")} />
                )}
                <Field label="Source" value={selected.source} />
                <Field label="Created" value={fmtDate(selected.created_at)} />
              </div>

              {selected.concept_id && (
                <button onClick={() => openReport(selected.concept_id)} data-testid="crm-open-report"
                  className="w-full btn-outline rounded-lg px-4 py-2.5 text-sm flex items-center justify-center gap-2">
                  <FileText size={14} /> Open redesign report (PDF)
                </button>
              )}

              {selected.proposal_id && (
                <button onClick={() => openProposal(selected.proposal_id)} data-testid="crm-open-proposal"
                  className="w-full btn-outline rounded-lg px-4 py-2.5 text-sm flex items-center justify-center gap-2">
                  <FileText size={14} /> Open growth proposal (PDF)
                </button>
              )}

              {selected.message && (
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Message</p>
                  <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap">{selected.message}</p>
                </div>
              )}

              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Status</p>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button key={s} onClick={() => changeStatus(selected, s)} data-testid={`crm-set-${s}`}
                      className={`text-xs font-semibold border rounded-lg px-3 py-1.5 transition-colors ${selected.status === s ? STATUS_META[s].cls : "border-slate-200 text-slate-500 hover:border-brand"}`}>
                      {STATUS_META[s].label}
                    </button>
                  ))}
                </div>
              </div>

              <OutreachPanel lead={selected} onOutreach={(next) => {
                setSelected((s) => ({ ...s, outreach: next }));
                setData((d) => ({ ...d, leads: d.leads.map((l) => (l.lead_key === selected.lead_key ? { ...l, outreach: next } : l)) }));
              }} />

              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Notes</p>
                <div className="space-y-2 mb-3">
                  {(selected.notes || []).length === 0 && <p className="text-xs text-slate-400">No notes yet.</p>}
                  {(selected.notes || []).map((n, i) => (
                    <div key={i} className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700" data-testid="crm-note">
                      <p className="whitespace-pre-wrap">{n.text}</p>
                      <p className="text-[10px] text-slate-400 mt-1">{n.by} · {fmtDate(n.at)}</p>
                    </div>
                  ))}
                </div>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Add a note…" data-testid="crm-note-input"
                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-brand" />
                <button onClick={addNote} disabled={savingNote || !note.trim()} data-testid="crm-note-save"
                  className="btn-primary rounded-lg px-4 py-2 text-sm mt-2 flex items-center gap-2 disabled:opacity-50">
                  {savingNote ? <Loader2 size={14} className="animate-spin" /> : <StickyNote size={14} />} Save note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const PipeCard = ({ active, label, value, onClick, testid, accent }) => (
  <button onClick={onClick} data-testid={testid}
    className={`text-left rounded-xl border p-3 transition-all ${active ? "border-brand ring-2 ring-brand/20 bg-white" : "border-slate-200 bg-white hover:border-slate-300"}`}>
    <p className="font-heading text-2xl font-extrabold text-slate-900">{value}</p>
    <p className={`text-[11px] font-semibold mt-0.5 inline-block px-1.5 py-0.5 rounded ${accent || "text-slate-500"}`}>{label}</p>
  </button>
);

const Field = ({ label, value }) => (
  value ? (
    <div className="flex justify-between gap-4">
      <span className="text-slate-400">{label}</span>
      <span className="text-slate-700 font-medium text-right break-all">{value}</span>
    </div>
  ) : null
);
